import React, { useContext, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import SaveIcon from '@mui/icons-material/Save';
import WifiIcon from '@mui/icons-material/Wifi';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PageLayout from '../../../components/layout/PageLayout';
import { EmailApi, ConfiguracionSmtp, GuardarSmtpPayload } from '../../../services/api/EmailApi';
import { DarkModeContext } from '../../../context/darkModeContext';

const DEFAULTS: ConfiguracionSmtp = {
  Host: '',
  Puerto: 587,
  Usuario: '',
  Clave: '',
  RemitenteName: '',
  RemitenteEmail: '',
  UsarTLS: true,
};

export const ConfiguracionSmtpPage: React.FC = () => {
  const { darkMode } = useContext(DarkModeContext);
  const [form, setForm] = useState<ConfiguracionSmtp>(DEFAULTS);
  const [clave, setClave] = useState('');
  const [mostrarClave, setMostrarClave] = useState(false);
  const [tieneClave, setTieneClave] = useState(false);
  const [loadingGuardar, setLoadingGuardar] = useState(false);
  const [loadingProbar, setLoadingProbar] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [snack, setSnack] = useState<{ open: boolean; msg: string; severity: 'success' | 'error' | 'info' }>({ open: false, msg: '', severity: 'success' });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    cargarConfig();
  }, []);

  const cargarConfig = async () => {
    setLoadingData(true);
    try {
      const data = await EmailApi.obtenerConfiguracionSmtp();
      if (data) {
        setForm({ ...data, Clave: '' });
        setTieneClave(!!data.tieneClave);
      }
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (field: keyof ConfiguracionSmtp) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validar = (): boolean => {
    const errs: Record<string, string> = {};
    if (!form.Host.trim()) errs.Host = 'Requerido';
    if (!form.Puerto || form.Puerto < 1 || form.Puerto > 65535) errs.Puerto = 'Puerto inválido (1-65535)';
    if (!form.Usuario.trim()) errs.Usuario = 'Requerido';
    if (!tieneClave && !clave.trim()) errs.Clave = 'Requerido (aún no hay clave guardada)';
    if (!form.RemitenteName.trim()) errs.RemitenteName = 'Requerido';
    if (!form.RemitenteEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.RemitenteEmail)) errs.RemitenteEmail = 'Correo inválido';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleGuardar = async () => {
    if (!validar()) return;
    setLoadingGuardar(true);
    try {
      await EmailApi.guardarConfiguracionSmtp({
        Host: form.Host,
        Puerto: Number(form.Puerto),
        Usuario: form.Usuario,
        Clave: clave,
        RemitenteName: form.RemitenteName,
        RemitenteEmail: form.RemitenteEmail,
        UsarTLS: form.UsarTLS,
      });
      setSnack({ open: true, msg: 'Configuración SMTP guardada correctamente', severity: 'success' });
      setTieneClave(true);
      setClave('');
      cargarConfig();
    } catch (e: any) {
      setSnack({ open: true, msg: e.message || 'Error al guardar', severity: 'error' });
    } finally {
      setLoadingGuardar(false);
    }
  };

  const handleProbar = async () => {
    setLoadingProbar(true);
    try {
      const res = await EmailApi.probarConexion();
      setSnack({ open: true, msg: res.mensaje, severity: res.ok ? 'success' : 'error' });
    } catch (e: any) {
      setSnack({ open: true, msg: e.message || 'Error al probar conexión', severity: 'error' });
    } finally {
      setLoadingProbar(false);
    }
  };

  const fieldProps = (label: string, field: keyof ConfiguracionSmtp, type = 'text') => ({
    label,
    value: (form[field] as string) || '',
    onChange: handleChange(field),
    error: !!errors[field],
    helperText: errors[field],
    type,
    fullWidth: true,
    size: 'small' as const,
    sx: {
      '& .MuiInputBase-root': {
        bgcolor: darkMode ? '#2b2b2b' : '#f7f7f5',
        borderRadius: '8px',
      },
    },
  });

  return (
    <PageLayout icon={EmailIcon} title="Configuración de Correo SMTP">
      <Box sx={{ p: 3, maxWidth: 640 }}>
        {loadingData ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper
            elevation={0}
            sx={{
              border: `1px solid ${darkMode ? '#333' : '#e9e9e7'}`,
              borderRadius: '12px',
              p: 3,
              bgcolor: darkMode ? '#202020' : '#fff',
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', mb: 0.5 }}>Servidor SMTP</Typography>
            <Typography sx={{ fontSize: '0.82rem', color: darkMode ? '#9b9a97' : '#6b6b6b', mb: 3 }}>
              La contraseña se almacena cifrada en la base de datos y nunca se expone en las respuestas del servidor.
            </Typography>

            <Stack spacing={2.5}>
              {/* Host y Puerto */}
              <Stack direction="row" spacing={2}>
                <TextField
                  {...fieldProps('Host SMTP', 'Host')}
                  sx={{ flex: 3, '& .MuiInputBase-root': { bgcolor: darkMode ? '#2b2b2b' : '#f7f7f5', borderRadius: '8px' } }}
                />
                <TextField
                  label="Puerto"
                  value={form.Puerto}
                  onChange={(e) => { setForm(p => ({ ...p, Puerto: Number(e.target.value) })); setErrors(p => ({ ...p, Puerto: '' })); }}
                  error={!!errors.Puerto}
                  helperText={errors.Puerto}
                  type="number"
                  size="small"
                  sx={{ flex: 1, '& .MuiInputBase-root': { bgcolor: darkMode ? '#2b2b2b' : '#f7f7f5', borderRadius: '8px' } }}
                />
              </Stack>

              {/* Usuario */}
              <TextField {...fieldProps('Usuario / Email de la cuenta SMTP', 'Usuario')} />

              {/* Clave */}
              <TextField
                label={tieneClave ? 'Nueva contraseña (dejar vacío para no cambiar)' : 'Contraseña'}
                value={clave}
                onChange={(e) => { setClave(e.target.value); setErrors(p => ({ ...p, Clave: '' })); }}
                error={!!errors.Clave}
                helperText={errors.Clave || (tieneClave ? 'Hay una contraseña guardada. Solo escribe aquí si deseas cambiarla.' : '')}
                type={mostrarClave ? 'text' : 'password'}
                fullWidth
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setMostrarClave(v => !v)}>
                        {mostrarClave ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiInputBase-root': { bgcolor: darkMode ? '#2b2b2b' : '#f7f7f5', borderRadius: '8px' } }}
              />

              <Divider />

              {/* Remitente */}
              <TextField {...fieldProps('Nombre del remitente', 'RemitenteName')} />
              <TextField {...fieldProps('Correo del remitente (From)', 'RemitenteEmail', 'email')} />

              {/* TLS */}
              <FormControlLabel
                control={
                  <Switch
                    checked={form.UsarTLS}
                    onChange={(e) => setForm(p => ({ ...p, UsarTLS: e.target.checked }))}
                    color="primary"
                  />
                }
                label={<Typography sx={{ fontSize: '0.88rem' }}>Usar TLS/STARTTLS</Typography>}
              />
            </Stack>

            {/* Acciones */}
            <Stack direction="row" spacing={1.5} mt={3}>
              <Button
                variant="contained"
                disableElevation
                onClick={handleGuardar}
                disabled={loadingGuardar}
                startIcon={loadingGuardar ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, bgcolor: '#2383e2', '&:hover': { bgcolor: '#1b6fc2' } }}
              >
                {loadingGuardar ? 'Guardando…' : 'Guardar'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleProbar}
                disabled={loadingProbar || !tieneClave}
                startIcon={loadingProbar ? <CircularProgress size={16} /> : <WifiIcon />}
                sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}
              >
                {loadingProbar ? 'Probando…' : 'Probar conexión'}
              </Button>
            </Stack>
            {!tieneClave && (
              <Typography sx={{ fontSize: '0.76rem', color: darkMode ? '#9b9a97' : '#6b6b6b', mt: 1 }}>
                Guarda la configuración primero para poder probar la conexión.
              </Typography>
            )}
          </Paper>
        )}
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack(p => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack(p => ({ ...p, open: false }))} sx={{ borderRadius: '8px' }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </PageLayout>
  );
};
